import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Download,
  FileSpreadsheet,
  FileText,
  FileWarning,
  Terminal,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { InvoiceRunStatus, InvoiceRunType } from '@/graphql/generatedTypes';
import { Routes } from '@/utils/routes';

import type { InvoiceRunDetailQuery } from './InvoiceRunDetail.generated';
import { useResultFileDownload } from './useResultFileDownload';
import {
  formatBytes,
  formatEsNumber,
  formatTimestamp,
  isEsStylePeriod,
  periodLabel,
  runTypeLongLabel,
  shortRunId,
  triggeredByLabel,
} from './invoiceRunPresentation';

type RunDetail = NonNullable<InvoiceRunDetailQuery['invoiceRun']>;
type RunFile = RunDetail['result_files'][number];

function RailCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="tw:gap-2 tw:py-4">
      <CardHeader className="tw:px-4">
        <CardTitle className="tw:text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="tw:px-4">{children}</CardContent>
    </Card>
  );
}

function RailRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="tw:flex tw:items-baseline tw:justify-between tw:gap-2 tw:py-1 tw:text-sm">
      <span className="tw:shrink-0 tw:text-muted-foreground">{label}</span>
      <span
        className={
          mono
            ? 'tw:text-right tw:font-mono tw:text-xs tw:tabular-nums'
            : 'tw:text-right'
        }
      >
        {value}
      </span>
    </div>
  );
}

function fileIcon(name: string) {
  const lower = name.toLowerCase();
  if (lower.endsWith('.xlsx') || lower.includes('breakdown'))
    return FileSpreadsheet;
  if (lower.endsWith('.log')) return Terminal;
  if (lower.includes('error')) return FileWarning;
  return FileText;
}

function ResultFilesCard({
  files,
  download,
  busyKey,
}: {
  files: RunFile[];
  download: (file: Pick<RunFile, 'key' | 'name'>) => void;
  busyKey: string | null;
}) {
  if (files.length === 0) return null;
  return (
    <RailCard title="Result files">
      <p className="tw:mb-2 tw:text-xs tw:text-muted-foreground">
        Generated fresh on download.
      </p>
      <div className="tw:flex tw:flex-col">
        {files.map((file) => {
          const Icon = fileIcon(file.name);
          const busy = busyKey === file.key;
          return (
            <button
              key={file.key}
              type="button"
              disabled={busy}
              onClick={() => download(file)}
              className="tw:group tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:rounded-md tw:px-1.5 tw:py-1.5 tw:text-left tw:transition-colors tw:hover:bg-muted"
            >
              <Icon className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
              <span className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
                <span
                  className="tw:truncate tw:text-xs tw:font-medium"
                  title={file.name}
                >
                  {file.name}
                </span>
                <span className="tw:text-[11px] tw:text-muted-foreground">
                  {formatBytes(file.size)}
                </span>
              </span>
              {busy ? (
                <Spinner className="tw:size-3.5" />
              ) : (
                <Download className="tw:size-3.5 tw:shrink-0 tw:text-muted-foreground tw:opacity-0 tw:transition-opacity tw:group-hover:opacity-100" />
              )}
            </button>
          );
        })}
      </div>
    </RailCard>
  );
}

function LinkedRunsCard({ run }: { run: RunDetail }) {
  const links: { label: string; text: string; runId: string }[] = [];
  if (run.charge_run) {
    links.push({
      label: 'Charged by',
      text: `Charge run ${shortRunId(run.charge_run.id)}`,
      runId: run.charge_run.id,
    });
  }
  if (run.parent_run) {
    links.push({
      label: 'Charges',
      text: `${periodLabel(
        isEsStylePeriod(run.parent_run),
        run.parent_run.year,
        run.parent_run.iso_month,
      )} · ${shortRunId(run.parent_run.id)}`,
      runId: run.parent_run.id,
    });
  }
  if (links.length === 0) return null;
  return (
    <RailCard title="Linked runs">
      <div className="tw:flex tw:flex-col tw:gap-1">
        {links.map((link) => (
          <Link
            key={link.runId}
            to={Routes.monthlyInvoicingRun(link.runId)}
            className="tw:flex tw:items-center tw:gap-1.5 tw:rounded-md tw:px-1.5 tw:py-1 tw:text-sm tw:no-underline tw:transition-colors tw:hover:bg-muted"
          >
            <span className="tw:text-muted-foreground">{link.label}</span>
            <ArrowRight className="tw:size-3.5 tw:text-muted-foreground" />
            <span className="tw:font-medium">{link.text}</span>
          </Link>
        ))}
      </div>
    </RailCard>
  );
}

export function RightRail({ run }: { run: RunDetail }) {
  const { download, busyKey } = useResultFileDownload();
  const isEs = run.type === InvoiceRunType.Es;
  const running = run.status === InvoiceRunStatus.Running;

  // The rail can outgrow the viewport (a run produces 5+ result files plus
  // parameters/timeline/linked runs), so it scrolls on its own instead of
  // clipping below the fold while sticky.
  return (
    <div className="tw:sticky tw:top-6 tw:flex tw:max-h-[calc(100vh-3rem)] tw:w-[284px] tw:shrink-0 tw:flex-col tw:gap-3.5 tw:self-start tw:overflow-y-auto tw:pr-1">
      <RailCard title="Parameters">
        <RailRow label="Run type" value={runTypeLongLabel(run.type)} />
        <RailRow
          label="Triggered by"
          value={triggeredByLabel(run.requested_by)}
        />
        <RailRow label="Dry run" value={run.dry_run ? 'Yes' : 'No'} />
        {isEs && (
          <>
            {/* The requested/used pair is the audit trail for the legal ES
                sequence — kept even when both are equal. */}
            <RailRow
              label="Requested number"
              mono
              value={
                run.requested_es_invoice_number != null
                  ? formatEsNumber(run.requested_es_invoice_number)
                  : 'auto'
              }
            />
            <RailRow
              label="Number used"
              mono
              value={
                run.resolved_es_invoice_number != null
                  ? formatEsNumber(run.resolved_es_invoice_number)
                  : '—'
              }
            />
          </>
        )}
      </RailCard>

      <RailCard title="Timeline">
        <RailRow
          label="Enqueued"
          mono
          value={formatTimestamp(run.enqueued_at)}
        />
        {run.due_at && (
          <RailRow label="Due" mono value={formatTimestamp(run.due_at)} />
        )}
        {run.started_at && (
          <RailRow
            label="Started"
            mono
            value={formatTimestamp(run.started_at)}
          />
        )}
        {run.finished_at ? (
          <RailRow
            label="Finished"
            mono
            value={formatTimestamp(run.finished_at)}
          />
        ) : (
          running &&
          run.last_activity_at && (
            <RailRow
              label="Last activity"
              mono
              value={formatTimestamp(run.last_activity_at)}
            />
          )
        )}
      </RailCard>

      <ResultFilesCard
        files={run.result_files}
        download={download}
        busyKey={busyKey}
      />

      <LinkedRunsCard run={run} />
    </div>
  );
}
